import { zodResolver } from "@hookform/resolvers/zod";

import { useForm } from "react-hook-form";
import { v4 as uuidv4 } from "uuid";

import { useTranslation } from "react-i18next";

import { useState } from "react";

import CircularProgress from "@mui/material/CircularProgress";

import { faTrashCan } from "@fortawesome/pro-regular-svg-icons";

import { Button } from "@/components/Button/Button";

import { ButtonColor } from "@/components/Button/types";

import FormTextField from "@/pages/Private/pages/Account/components/FormTextField";

import { useGetIndustriesQuery } from "@/pages/Private/redux/industry/industry.api";

import { useGetCompanyRevenuesQuery } from "@/pages/Private/redux/company-revenue/company-revenue.api";
import { useGetCompanySizesQuery } from "@/pages/Private/redux/company-size/company-size.api";

import { useGetAllTagsQuery } from "@/pages/Private/redux/to-do/to-do.api";

import { AutoComplete, AutoCompleteItem } from "@/components/AutoComplete/AutoComplete";

import { Icon } from "@/components/Icon/Icon";

import { useGetCompanyBalancesQuery } from "@/pages/Private/redux/company-balance/company-balance.api";

import { ToDoInfo, ToDoSubTaskInfo, ToDoTagInfo } from "../../../ToDoList/schema/to-do";
import FormLabel from "./FormLabel";
import RichTextEditor from "./RichTextEditor";
import TypeRadioGroup from "./TypeRadioGroup";
import { ShowingMode } from "../../../ToDoList/enum/ShowingMode.enum";
import IndustrySelectorFactory from "../IndustrySelectorFactory";
import CompanySizeSelectorFactory from "../CompanySizeSelectorFactory";
import CompanyRevenueSelectorFactory from "../CompanyRevenueSelectorFactory";
import { ToDoPriority } from "../../../ToDoList/enum/ToDoPriority.enum";
import CompanyBalanceSelectorFactory from "../CompanyBalanceSelectorFactory";

interface EditModeToDoItemProps {
	isLoading?: boolean;
	data?: ToDoInfo;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	validationSchema: any;
	setEditing: (value: number) => void;
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onSubmit: (values: any) => void;
}

export default function EditModeToDoItem({
	data,
	validationSchema,
	setEditing,
	onSubmit,
	isLoading,
}: EditModeToDoItemProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`manage-to-dos.${key}`);
	const { data: tags, isLoading: isTagsLoading } = useGetAllTagsQuery();
	const [currentFormState, setCurrentFormState] = useState(data);

	const { data: industries = [] } = useGetIndustriesQuery();
	const filteredIndustries = (industries ?? []).filter(
		(industry): industry is { id: number; name: string } => typeof industry.id === "number"
	);

	const { data: companySizes = [] } = useGetCompanySizesQuery();
	const filteredCompanySizes = (companySizes ?? []).filter(
		(companySize): companySize is { id: number; value: string } =>
			typeof companySize.id === "number"
	);

	const { data: companyRevenues = [] } = useGetCompanyRevenuesQuery();
	const filteredCompanyRevenues = (companyRevenues ?? []).filter(
		(companyRevenue): companyRevenue is { id: number; value: string } =>
			typeof companyRevenue.id === "number"
	);

	const { data: companyBalances = [] } = useGetCompanyBalancesQuery();
	const filteredCompanyBalances = (companyBalances ?? []).filter(
		(companyBalance): companyBalance is { id: number; value: string } =>
			typeof companyBalance.id === "number"
	);

	const handleSetTags = (value?: AutoCompleteItem) => {
		if (value && value?.title) {
			handleSaveFormState(
				{
					id: value?.id ?? -1,
					name: value.title,
				} as ToDoTagInfo,
				"tag"
			);
		}
	};

	const {
		register,
		formState: { errors },
		setValue,
		watch,
		handleSubmit,
	} = useForm({
		defaultValues: {
			...data,
			industryIds: data?.industryIds ?? [],
			companySizeIds: data?.companySizeIds ?? [],
			companyRevenueIds: data?.companyRevenueIds ?? [],
			companyBalanceIds: data?.companyBalanceIds ?? [],
			isOutOfDate: data?.isOutOfDate ? ShowingMode.OUT_OF_DATE : ShowingMode.ONLY_NEW,
			priority: data?.priority ?? ToDoPriority.MEDIUM,
			subTasks: data?.subTasks ?? [],
			requirement: data?.requirement ?? "",
		},
		mode: "onBlur",
		resolver: zodResolver(validationSchema),
	});

	const formValues = watch();

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleSaveFormState = (value: any, name: keyof ToDoInfo) => {
		setCurrentFormState({
			...currentFormState,
			[name]: value,
		});

		setValue(name, value, {
			shouldDirty: true,
			shouldValidate: true,
		});
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleFormSubmit = (values: any) => {
		const payload = {
			...values,
			isOutOfDate: values.isOutOfDate === ShowingMode.OUT_OF_DATE,
		};

		onSubmit(payload);
	};

	const handleAddSubTask = (subTasks: ToDoSubTaskInfo[]) => {
		setValue("subTasks", [
			...(subTasks || []),
			{ id: uuidv4(), title: "", description: "", question: "", questionDescription: "" },
		]);
	};

	return (
		<form
			className="w-full rounded-xl p-4 bg-white"
			style={{ boxShadow: "0px 1px 2px 0px #1C274C1F" }}
		>
			{(isLoading || isTagsLoading) && (
				<div className="h-96 flex items-center justify-center">
					<CircularProgress className="!w-14 !h-14" />
				</div>
			)}
			{!isLoading && !isTagsLoading && (
				<>
					<div className="grid grid-cols-5 gap-y-5">
						<FormLabel className="mt-4" title={ts("form-fields.title")} />
						<FormTextField
							autoComplete="title"
							containerClassName="col-span-4 !mb-0"
							errorMessage={errors?.title?.message}
							formRegister={register("title")}
							label={ts("form-fields.title")}
							showLabel={false}
						/>
						<FormLabel className="mt-4" title={ts("form-fields.requirement")} />
						<FormTextField
							autoComplete="requirement"
							containerClassName="col-span-4 !mb-0"
							errorMessage={errors?.requirement?.message}
							formRegister={register("requirement")}
							label={ts("form-fields.requirement")}
							showLabel={false}
						/>

						<FormLabel className="mt-4" title={ts("form-fields.category")} />
						<AutoComplete
							containerClassName="col-span-4"
							data={
								tags
									?.filter((tag) => !!tag?.id && !!tag?.name)
									?.map((tag) => ({ id: tag?.id ?? -1, title: tag?.name ?? "" })) ?? []
							}
							errorMessage={errors?.tag?.message}
							// eslint-disable-next-line @typescript-eslint/no-empty-function
							handleSearch={() => {}}
							handleSelect={handleSetTags}
							position="bottom"
							selectedItem={currentFormState?.tag?.name ?? ""}
						/>

						<FormLabel className="mt-4" title={ts("select-industries")} />
						<IndustrySelectorFactory
							containerClassName="col-span-4 !mb-0"
							error={errors?.industryIds?.message}
							industries={filteredIndustries}
							setValue={setValue}
							watch={watch}
						/>
						<FormLabel className="mt-4" title={ts("select-company-sizes")} />
						<CompanySizeSelectorFactory
							companySizes={filteredCompanySizes}
							containerClassName="col-span-4 !mb-0"
							error={errors?.companySizeIds?.message}
							setValue={setValue}
							watch={watch}
						/>
						<FormLabel className="mt-4" title={ts("select-company-revenues")} />
						<CompanyRevenueSelectorFactory
							companyRevenues={filteredCompanyRevenues}
							containerClassName="col-span-4 !mb-0"
							error={errors?.companyRevenueIds?.message}
							setValue={setValue}
							watch={watch}
						/>
						<FormLabel className="mt-4" title={ts("select-company-balances")} />
						<CompanyBalanceSelectorFactory
							companyBalances={filteredCompanyBalances}
							containerClassName="col-span-4 !mb-0"
							error={errors?.companyBalanceIds?.message}
							setValue={setValue}
							watch={watch}
						/>
						{!data?.id && (
							<>
								<FormLabel className="mt-4" title={ts("form-fields.showing-mode")} />
								<TypeRadioGroup
									className="col-span-4"
									errorMessage={errors?.showingMode?.message}
									formRegister={register("showingMode")}
									options={[ShowingMode.ALL, ShowingMode.ONLY_NEW]}
									value={watch("showingMode")}
								/>
							</>
						)}
						<FormLabel className="mt-4" title={ts("form-fields.priority")} />
						<TypeRadioGroup
							className="col-span-4"
							errorMessage={errors?.priority?.message}
							formRegister={register("priority")}
							needIcon={false}
							options={Object.values(ToDoPriority)}
							value={watch("priority")}
						/>
						{!!data?.id && (
							<>
								<FormLabel className="mt-4" title={ts("form-fields.out-of-date")} />
								<TypeRadioGroup
									className="col-span-4"
									errorMessage={errors?.isOutOfDate?.message}
									formRegister={register("isOutOfDate")}
									needIcon={false}
									options={[ShowingMode.OUT_OF_DATE, ShowingMode.ONLY_NEW]}
									value={watch("isOutOfDate")}
								/>
							</>
						)}
						<FormLabel className="mt-4" title={ts("form-fields.measureDescription")} />
						<RichTextEditor
							className="col-span-4"
							errorMessage={errors?.description?.message}
							placeholder={ts("about-this-task")}
							setValue={(value) => handleSaveFormState(value, "description")}
							value={watch("description")}
						/>
						<FormLabel className="mt-4" title={ts("form-fields.measureRecommendation")} />
						<RichTextEditor
							className="col-span-4"
							errorMessage={errors?.recommendation?.message}
							placeholder={ts("enter-recommendation")}
							setValue={(value) => {
								if (value === "<p><br></p>") {
									handleSaveFormState(null, "recommendation");
								} else {
									handleSaveFormState(value, "recommendation");
								}
							}}
							value={watch("recommendation")}
						/>
						<FormLabel className="mt-4" title={ts("form-fields.question")} />
						<FormTextField
							containerClassName="col-span-4 !mb-0"
							errorMessage={errors?.question?.message}
							formRegister={register("question")}
							showLabel={false}
						/>
					</div>
					<div className="flex flex-col gap-y-5 mt-8 pt-4 border-t border-gray-200">
						<FormLabel className="mt-4  text-[16px]" title={ts("form-fields.sub-tasks")} />

						{formValues?.subTasks && formValues?.subTasks?.length > 0 && (
							<>
								<div className=" ">
									{formValues?.subTasks?.map((subTask, index) => (
										<div
											key={`${subTask?.id}`}
											className="flex flex-col items-start justify-between gap-2 mb-4 pb-4 w-full border-gray-200 border-b-2 last:border-none"
										>
											<div className="grid grid-cols-5 gap-y-5 w-full">
												<FormLabel className="mt-4" title={ts("form-fields.subTaskTitle")} />
												<FormTextField
													containerClassName="col-span-3 !mb-0"
													errorMessage={errors?.subTasks?.[index]?.title?.message}
													formRegister={register(`subTasks.${index}.title`)}
													showLabel={false}
												/>
												<Button
													className="col-span-1 justify-self-end rounded-[12px] bg-white !min-w-[44px] !min-h-[44px] !max-w-[44px] !max-h-[44px] !p-0 !border-[0.28px] !border-[#E6E6EC] hover:bg-primary-gray-lightest"
													image={
														<Icon className="text-primary-blue-dark w-5 h-5" icon={faTrashCan} />
													}
													title=""
													onClick={() => {
														const newSubTasks = formValues?.subTasks?.filter((_, i) => i !== index);

														setValue("subTasks", newSubTasks);
													}}
												/>
												<FormLabel className="mt-4" title={ts("form-fields.definition")} />
												<RichTextEditor
													className="col-span-4"
													errorMessage={errors?.subTasks?.[index]?.description?.message}
													placeholder={ts("enter-recommendation")}
													setValue={(value) => {
														const newSubTasks = [...formValues?.subTasks];

														newSubTasks[index].description = value;

														setValue("subTasks", newSubTasks);
													}}
													value={watch(`subTasks.${index}.description`)}
												/>
												<FormLabel className="mt-4" title={ts("form-fields.question")} />
												<FormTextField
													containerClassName="col-span-4 !mb-0"
													errorMessage={errors?.subTasks?.[index]?.question?.message}
													formRegister={register(`subTasks.${index}.question`)}
													showLabel={false}
												/>
												<FormLabel className="mt-4" title={ts("form-fields.subTaskInfo")} />
												<RichTextEditor
													className="col-span-4"
													errorMessage={errors?.subTasks?.[index]?.questionDescription?.message}
													placeholder={ts("enter-recommendation")}
													setValue={(value) => {
														const newSubTasks = [...formValues?.subTasks];

														newSubTasks[index].questionDescription = value;

														setValue("subTasks", newSubTasks);
													}}
													value={watch(`subTasks.${index}.questionDescription`)}
												/>
											</div>
										</div>
									))}
								</div>
							</>
						)}
						<div className="w-[200px] relative">
							<Button
								className="  !min-h-[44px] !max-h-[44px]"
								color={ButtonColor.ACTION}
								title={ts("form-fields.add-sub-task")}
								type="button"
								onClick={() => handleAddSubTask(formValues?.subTasks)}
							/>
						</div>
					</div>

					<div className="flex items-center justify-end gap-2 mt-6">
						<Button
							className="!min-w-[110px] !max-w-[110px] !min-h-[44px] !max-h-[44px]"
							color={ButtonColor.ACTION_SECONDARY}
							title={t("basics.cancel")}
							type="button"
							onClick={() => setEditing(-1)}
						/>
						<Button
							className="!min-w-[110px] !max-w-[110px] !min-h-[44px] !max-h-[44px]"
							color={ButtonColor.ACTION}
							disabled={isLoading}
							isLoading={isLoading}
							title={data?.id ? t("basics.save") : t("basics.add")}
							type="button"
							onClick={handleSubmit(handleFormSubmit)}
						/>
					</div>
				</>
			)}
		</form>
	);
}
